/**
 * Cloudflare Turnstile React widget.
 *
 * Why a one-off component instead of a 3rd-party library:
 *  - The official @marsidev/react-turnstile package adds 30+ KB for what is
 *    essentially `window.turnstile.render(...)`. We render in maybe two
 *    places and don't need its abstractions.
 *  - Keeps our supply-chain surface narrow.
 *
 * Behaviour:
 *  - Loads the Turnstile script tag exactly once per page lifetime.
 *  - Re-renders the widget when `siteKey` changes.
 *  - Emits the token via `onToken(token | null)` — null when expired/reset.
 *  - Auto-resets the widget when the parent calls the imperative `reset()`
 *    handle (exposed via the `widgetRef` prop) — used after a failed
 *    submission so the user can try again with a fresh token.
 */
import { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise = null;

function loadTurnstile() {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => reject(new Error("turnstile-script-failed"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  theme = "dark",
  size = "normal", // 'normal' | 'flexible' | 'compact' | 'invisible'
  appearance = "always", // 'always' | 'execute' | 'interaction-only'
  widgetRef,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !containerRef.current || !ts) return;
        widgetIdRef.current = ts.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          appearance,
          callback: (token) => onToken && onToken(token),
          "expired-callback": () => onToken && onToken(null),
          "error-callback": () => onToken && onToken(null),
        });
        if (widgetRef) {
          widgetRef.current = {
            reset: () => widgetIdRef.current && ts.reset(widgetIdRef.current),
          };
        }
      })
      .catch(() => onToken && onToken(null));
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* idempotent */ }
      }
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" />;
}

export default TurnstileWidget;
